"use strict";
/*
  based on: https://github.com/rzeigler/waveguide/blob/master/src/semaphore.ts
  credits to original author
 */
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
exports.__esModule = true;
var fp_ts_1 = require("fp-ts");
var dequeue_1 = require("./original/support/dequeue");
var deferred_1 = require("./deferred");
var ref_1 = require("./ref");
var ticket_1 = require("./ticket");
var T = __importStar(require("./effect"));
var effect_1 = require("./effect");
var isReservationFor = function (latch) { return function (rsv) { return rsv[1] === latch; }; };
function sanityCheck(n) {
    if (n < 0) {
        return T.raiseAbort(new Error("Die: semaphore permits must be non negative"));
    }
    if (Math.round(n) !== n) {
        return T.raiseAbort(new Error("Die: semaphore permits may not be fractional"));
    }
    return T.unit;
}
function makeSemaphoreImpl(ref) {
    var releaseN = function (n) {
        return T.applySecond(sanityCheck(n), T.uninterruptible(n === 0
            ? T.unit
            : T.flatten(ref.modify(function (current) {
                return fp_ts_1.pipeable.pipe(current, fp_ts_1.either.fold(function (waiting) {
                    return fp_ts_1.pipeable.pipe(waiting.take(), fp_ts_1.option.fold(function () { return [T.unit, fp_ts_1.either.right(n)]; }, function (_a) {
                        var _b = _a[0], needed = _b[0], latch = _b[1], q = _a[1];
                        return n >= needed
                            ? [
                                T.applyFirst(latch.done(undefined), n > needed ? releaseN(n - needed) : T.unit),
                                fp_ts_1.either.left(q)
                            ]
                            : [T.unit, fp_ts_1.either.left(q.push([needed - n, latch]))];
                    }));
                }, function (ready) { return [T.unit, fp_ts_1.either.right(ready + n)]; }));
            }))));
    };
    var cancelWait = function (n, latch) {
        return T.uninterruptible(T.flatten(ref.modify(function (current) {
            return fp_ts_1.pipeable.pipe(current, fp_ts_1.either.fold(function (waiting) {
                return fp_ts_1.pipeable.pipe(waiting.find(isReservationFor(latch)), fp_ts_1.option.fold(function () { return [releaseN(n), fp_ts_1.either.left(waiting)]; }, function (_a) {
                    var pending = _a[0];
                    return [
                        releaseN(n - pending),
                        fp_ts_1.either.left(waiting.filter(fp_ts_1["function"].not(isReservationFor(latch))))
                    ];
                }));
            }, function (ready) { return [T.unit, fp_ts_1.either.right(ready + n)]; }));
        })));
    };
    var ticketN = function (n) {
        return effect_1.effect.chain(deferred_1.makeDeferred(), function (latch) {
            return ref.modify(function (current) {
                return fp_ts_1.pipeable.pipe(current, fp_ts_1.either.fold(function (waiting) {
                    return [
                        ticket_1.makeTicket(latch.wait, cancelWait(n, latch)),
                        fp_ts_1.either.left(waiting.offer([n, latch]))
                    ];
                }, function (ready) {
                    return ready >= n
                        ? [ticket_1.makeTicket(T.unit, releaseN(n)), fp_ts_1.either.right(ready - n)]
                        : [
                            ticket_1.makeTicket(latch.wait, cancelWait(n, latch)),
                            fp_ts_1.either.left(dequeue_1.empty().offer([n - ready, latch]))
                        ];
                }));
            });
        });
    };
    var acquireN = function (n) {
        return T.applySecond(sanityCheck(n), n === 0 ? T.unit : T.bracketExit(ticketN(n), ticket_1.ticketExit, ticket_1.ticketUse));
    };
    var withPermitsN = function (n, inner) {
        var acquire = T.interruptible(acquireN(n));
        var release = releaseN(n);
        return T.bracket(acquire, fp_ts_1["function"].constant(release), function () { return inner; });
    };
    var available = effect_1.effect.map(ref.get, fp_ts_1.either.fold(function (q) { return -1 * q.size(); }, fp_ts_1["function"].identity));
    return {
        acquireN: acquireN,
        acquire: acquireN(1),
        releaseN: releaseN,
        release: releaseN(1),
        withPermitsN: withPermitsN,
        withPermit: function (inner) { return withPermitsN(1, inner); },
        available: available
    };
}
/**
 * Allocate a semaphore.
 *
 * @param n the number of permits
 * This must be non-negative
 */
function makeSemaphore(n) {
    return T.applySecond(sanityCheck(n), effect_1.effect.map(ref_1.makeRef(fp_ts_1.either.right(n)), makeSemaphoreImpl));
}
exports.makeSemaphore = makeSemaphore;
